import { Component, StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

/**
 * Last line of defence. If anything in the tree throws, React would otherwise unmount everything
 * and leave a blank page -- the one outcome this diagnostic tool must never produce. The fallback
 * drops all formatting and shows the raw URL, which is the single fact worth rescuing.
 */
class LastResort extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("[inspector] render failed", error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    let href = "";
    let search = "";
    try {
      href = window.location.href;
      search = window.location.search;
    } catch {
      /* nothing left to read */
    }
    return (
      <main>
        <h1>Query Parameter Inspector</h1>
        <p className="warn">
          The page failed to render, so here is the raw URL on its own. The message below is the
          rendering fault, not a problem with the URL.
        </p>
        <h2>Full URL</h2>
        <pre className="raw-block">{href}</pre>
        <h2>Query string</h2>
        <pre className="raw-block">{search === "" ? "(no query string)" : search}</pre>
        <p className="meta">{this.state.error.message}</p>
      </main>
    );
  }
}

const container = document.getElementById("root");
if (container) {
  createRoot(container).render(
    <StrictMode>
      <LastResort>
        <App />
      </LastResort>
    </StrictMode>,
  );
}
